import { useState, type FormEvent } from "react";
import { AppleSignInButton } from "../components/AppleSignInButton.tsx";
import { GoogleSignInButton } from "../components/GoogleSignInButton.tsx";
import { PasswordInput } from "../components/PasswordInput.tsx";
import { BackButton } from "../components/BackButton.tsx";
import { ApiError, post } from "../lib/api.ts";
import { useSession, type IdentityProvider, type IdentitySignInResult } from "../lib/session.ts";
import { messages } from "../i18n/index.ts";
import { APP_EDITION } from "../lib/edition.ts";

type Mode = "login" | "register";

interface AuthScreenProps {
  onBack: () => void;
  /** Neden giriş isteniyor — kullanıcı bağlamı kaybetmesin. */
  reason?: string;
}

/** Google ya da Apple ile ilk giriş: kimlik doğrulandı, kullanıcı adı bekleniyor. */
interface PendingIdentity {
  provider: IdentityProvider;
  ticket: string;
  email: string;
}

export function AuthScreen({ onBack, reason }: AuthScreenProps) {
  const [mode, setMode] = useState<Mode>("login");
  const [login, setLogin] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordAgain, setPasswordAgain] = useState("");
  const [pendingIdentity, setPendingIdentity] = useState<PendingIdentity | null>(null);
  const [identityUsername, setIdentityUsername] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [recoveryKind, setRecoveryKind] = useState<"password" | "username" | null>(null);
  const [recoveryDone, setRecoveryDone] = useState(false);

  const session = useSession();

  // Yazarken uyarma; ikinci alana bir şey girildiyse eşleşmeyi göster.
  const mismatch = mode === "register" && passwordAgain.length > 0 && password !== passwordAgain;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (mode === "register" && password !== passwordAgain) {
      setError(messages.auth.passwordMismatch);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      if (mode === "login") {
        await session.login(login, password);
      } else {
        await session.register(email, username, password);
      }
      onBack();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : messages.auth.connectionFailed);
    } finally {
      setBusy(false);
    }
  }

  async function onIdentity(provider: IdentityProvider, signIn: () => Promise<IdentitySignInResult>) {
    setError(null);
    setBusy(true);
    try {
      const result = await signIn();
      if (result.status === "needs-username") {
        setPendingIdentity({ provider, ticket: result.ticket, email: result.email });
        setIdentityUsername("");
        return;
      }
      onBack();
    } catch (err) {
      setError(err instanceof ApiError ? err.message
        : provider === "google" ? messages.auth.googleFailed : messages.auth.appleFailed);
    } finally {
      setBusy(false);
    }
  }

  async function submitIdentityUsername(event: FormEvent) {
    event.preventDefault();
    if (!pendingIdentity) return;
    setError(null);
    setBusy(true);
    try {
      await session.completeSignUp(pendingIdentity.provider, pendingIdentity.ticket, identityUsername);
      onBack();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : messages.auth.createFailed);
    } finally {
      setBusy(false);
    }
  }

  const errorBox = error && (
    <p
      className="rounded-lg border border-flame/40 bg-flame/10 px-3 py-2 text-sm text-flame"
      role="alert"
    >
      {error}
    </p>
  );

  /* --- Google / Apple ile ilk giriş: kullanıcı adı adımı ----------- */
  if (pendingIdentity) {
    return (
      <div className="app">
        <header className="flex items-center gap-2 border-b border-line px-3 py-2.5">
          <BackButton onClick={() => setPendingIdentity(null)} />
          <span className="font-display text-base font-bold">{messages.auth.chooseUsername}</span>
        </header>

        <main className="overflow-y-auto px-5 py-6">
          <div className="mx-auto w-full max-w-sm">
            <p className="mb-5 text-sm leading-relaxed text-muted">
              {messages.auth.googleStep(pendingIdentity.email)}
            </p>

            <form onSubmit={submitIdentityUsername} className="flex flex-col gap-3">
              <label className="field">
                <span>{messages.auth.username}</span>
                <input
                  value={identityUsername}
                  onChange={(e) => setIdentityUsername(e.target.value)}
                  autoComplete="username"
                  autoCapitalize="none"
                  minLength={3}
                  maxLength={20}
                  autoFocus
                  required
                />
                <small>{messages.auth.usernameRule}</small>
              </label>

              {errorBox}

              <button type="submit" className="btn-primary mt-2" disabled={busy}>
                {busy ? messages.auth.wait : messages.auth.createAccount}
              </button>
            </form>
          </div>
        </main>
      </div>
    );
  }

  if (recoveryKind) {
    return <div className="app">
      <header className="flex items-center gap-2 border-b border-line px-3 py-2.5">
        <BackButton onClick={() => { setRecoveryKind(null); setError(null); setRecoveryDone(false); }} />
        <span className="font-display text-base font-bold">{recoveryKind === "password" ? messages.auth.forgotPassword : messages.auth.forgotUsername}</span>
      </header>
      <main className="overflow-y-auto px-5 py-6"><div className="mx-auto w-full max-w-sm">
        <p className="mb-4 text-sm text-muted">{messages.auth.recoveryHint}</p>
        <form className="flex flex-col gap-3" onSubmit={(event) => {
          event.preventDefault();
          setBusy(true); setError(null);
          void post("/api/auth/recovery/request", { email, kind: recoveryKind }).then(() => {
            setRecoveryDone(true);
          }).catch((err: unknown) => {
            setError(err instanceof ApiError ? err.message : messages.auth.connectionFailed);
          }).finally(() => setBusy(false));
        }}>
          <label className="field"><span>{messages.auth.email}</span><input type="email" value={email}
            onChange={(event) => setEmail(event.target.value)} autoComplete="email" required /></label>
          {errorBox}
          {recoveryDone && <p role="status" className="text-sm text-mint">{messages.auth.recoverySent}</p>}
          <button type="submit" className="btn-primary" disabled={busy}>{busy ? messages.auth.wait : messages.auth.sendRecovery}</button>
        </form>
      </div></main>
    </div>;
  }

  return (
    <div className="app">
      <header className="flex items-center gap-2 border-b border-line px-3 py-2.5">
        <BackButton onClick={onBack} />
        <span className="font-display text-base font-bold">
          {mode === "login" ? messages.auth.signIn : messages.auth.register}
        </span>
      </header>

      <main className="overflow-y-auto px-5 py-6">
        <div className="mx-auto w-full max-w-sm">
          {reason && <p className="mb-5 text-sm text-muted">{reason}</p>}

          {/* Yalnızca iOS uygulamasında görünür. */}
          <AppleSignInButton
            onCredential={(credential) => void onIdentity("apple", () => session.signInWithApple(credential))}
            onError={() => setError(messages.auth.appleFailed)}
            disabled={busy}
          />

          {/* Google kapalıysa ya da betik yüklenemezse burası boş kalır. */}
          <GoogleSignInButton
            onCredential={(credential) => void onIdentity("google", () => session.signInWithGoogle(credential))}
            text={mode === "login" ? "signin_with" : "signup_with"}
            disabled={busy}
            dividerLabel={messages.auth.or}
          />

          <form onSubmit={submit} className="flex flex-col gap-3">
            {mode === "login" ? (
              <label className="field">
                <span>{messages.auth.loginField}</span>
                <input
                  value={login}
                  onChange={(e) => setLogin(e.target.value)}
                  autoComplete="username"
                  autoCapitalize="none"
                  required
                />
              </label>
            ) : (
              <>
                <label className="field">
                  <span>{messages.auth.email}</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    autoCapitalize="none"
                    required
                  />
                </label>
                <label className="field">
                  <span>{messages.auth.username}</span>
                  <input
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    autoCapitalize="none"
                    minLength={3}
                    maxLength={20}
                    required
                  />
                  <small>{messages.auth.usernameHint}</small>
                </label>
              </>
            )}

            <label className="field">
              <span>{messages.auth.password}</span>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                minLength={mode === "register" ? 8 : undefined}
                required
              />
              {mode === "register" && <small>{messages.auth.passwordMin}</small>}
            </label>

            {mode === "register" && (
              <label className="field">
                <span>{messages.auth.repeatPassword}</span>
                <PasswordInput
                  value={passwordAgain}
                  onChange={(e) => setPasswordAgain(e.target.value)}
                  autoComplete="new-password"
                  aria-invalid={mismatch || undefined}
                  required
                />
                {mismatch && <small className="text-flame">{messages.auth.passwordMismatch}</small>}
              </label>
            )}

            {errorBox}

            <button type="submit" className="btn-primary mt-2" disabled={busy || mismatch}>
              {busy
                ? messages.auth.wait
                : mode === "login"
                  ? messages.auth.signIn
                  : messages.auth.createAccount}
            </button>
          </form>

          {mode === "login" && <div className="mt-1 flex justify-between gap-3 text-sm text-muted">
            <button type="button" className="text-link" onClick={() => { setRecoveryKind("password"); setError(null); }}>{messages.auth.forgotPassword}</button>
            <button type="button" className="text-link" onClick={() => { setRecoveryKind("username"); setError(null); }}>{messages.auth.forgotUsername}</button>
          </div>}

          {mode === "register" && (
            <p className="mt-3 text-center text-xs leading-relaxed text-dim">
              {messages.auth.privacyPrefix}{" "}
              <a
                className="underline underline-offset-4"
                href={APP_EDITION === "en" ? "/privacy" : "/gizlilik"}
                target="_blank"
                rel="noreferrer"
              >
                {messages.auth.privacyPolicy}
              </a>{" "}
              {messages.auth.privacySuffix}
            </p>
          )}

          <button
            type="button"
            className="text-link mt-3 w-full justify-center text-sm text-muted"
            onClick={() => {
              setMode(mode === "login" ? "register" : "login");
              setPasswordAgain("");
              setError(null);
            }}
          >
            {mode === "login" ? messages.auth.needAccount : messages.auth.haveAccount}
          </button>
        </div>
      </main>
    </div>
  );
}
